import * as Sentry from '@sentry/react';
import { serializeError } from 'serialize-error';
import { logger } from 'utils/logger';
import { noop } from 'utils/flow';
import { emitTelemetryEvent } from 'utils/sentry/telemetryEvent';
import {
  ActivateBackgroundOptions,
  BackgroundConfig,
  InputMediaOptions,
  VirtualBackgroundProvider,
} from './VirtualBackgroundProvider';
import { probeWasm, probeWebGL, probeWebGpu } from './capabilityProbe';
import {
  buildGateRecord,
  emitGateRecord,
  GateProbeState,
  GateRecord,
  gateVerdict,
  logsAttributes,
} from './gateTelemetry';
import { VBGateAbortedError, VBGateRefusedError } from './gateErrors';

const VB_LOG = { system: true, capture: 'virtual-background' } as const;

// The licence POST is served same-origin: haproxy matches `path_beg /vb/session` on the shared
// https frontend — host-agnostic, ahead of `use_backend react` — and rewrites /vb/ -> /sdk/ onto
// the EffectsSDK licence server. Same-origin removes the CORS preflight that every init used to
// pay (`Tsvb-Version` is a non-simple header and the response carries no `Access-Control-Max-Age`,
// so the OPTIONS was effectively uncached) plus a second DNS+TCP+TLS handshake, both of which
// landed concurrently with gUM, signalling and the multi-MB model download.
//
// REACT_APP_EFFECTS_SDK_LICENSE_URL stays an escape hatch for deployments without that route: an
// absolute value is used verbatim (`new URL` ignores the base), a path resolves against the origin.
// The trailing slash is mandatory — the SDK concatenates api_url + customer id.
const licenseUrl = (): string =>
  new URL(
    process.env.REACT_APP_EFFECTS_SDK_LICENSE_URL || '/vb/session/',
    window.location.origin
  ).href.replace(/\/?$/, '/');

// Numeric error codes from effects-sdk ErrorCode enum that indicate fatal initialization
// failures. GPU codes (1001, 1010) are excluded — the SDK auto-recovers via CPU fallback.
const FATAL_INIT_CODES = new Set<number>([
  1002, // APP_CREATION_ISSUE — renderer broken
  1020, // SESSION_INITIALIZATION_FAILED — server rejected
  1021, // SESSION_INITIALIZATION_ISSUE — network/WASM unreachable
  1022, // MODEL_INITIALIZATION_ISSUE — model didn't load
  1023, // LOADER_ISSUE — can't load model file
  1030, // NO_VIDEO_TRACK — no video in stream
  1066, // WASM_RUNTIME_LOAD_FAILED — ONNX runtime WASM binary unreachable
]);

// ±50 % jitter (total 6.5–19.5 s). A room-wide broadcast grant preloads every participant at once
// and the retryable codes fail correlated, so a narrow band would re-hit a failing route in lockstep.
const PRELOAD_RETRY_DELAYS_MS = [1000, 3000, 9000];

// Transient init failures the SDK's background session init can recover from on a re-run.
// Strict subset of FATAL_INIT_CODES: 1020 (licence status ≠ active), 1002, 1030 are not transient.
const PRELOAD_RETRYABLE_CODES = new Set<number>([1021, 1022, 1023, 1066]);

// Vendor Temporary-Issue codes (see General-Error-Handling.md). Set membership THROTTLES
// capture: a single occurrence is suppressed and only a persistence burst (≥2 within the window)
// is captured once per session. Removing a code here does NOT silence it — an error-type code not
// in this set falls through to unconditional capture. 1001/1010 (GPU loss) and 3030 (render-
// context lost) auto-recover, so throttling keeps benign singles quiet while surfacing thrashing.
const TEMPORARY_ISSUE_CODES = new Set<number>([
  1001, // GPU_DEVICE_LOST
  1010, // GPU_UNCAPTUREDERROR
  1031, // VIDEO_PLAYBACK_ISSUE
  1040, // INFERENCE_RUN_ISSUE
  1050, // RENDERING_ISSUE
  1060, // ENQUEUE_FRAME_ISSUE
  1061, // READABLE_STREAM_ISSUE
  3030, // RENDER_CONTEXT_LOST
]);

const PERSISTENCE_WINDOW_MS = 3000;

// 0.5/1/2/3/5×5 s ≈ 31.5 s: catches the 3.8 s median recovery fast, covers the
// 29,584 ms max observed.
const REPROBE_DELAYS_MS = [500, 1000, 2000, 3000, 5000, 5000, 5000, 5000, 5000];

// SOFTWARE_RENDERER_SKIPPED — the SDK detected a software WebGL renderer at runtime and
// skipped effects instead of emitting frozen/green frames. Captured once as the fallback signal.
const SOFTWARE_RENDERER_SKIPPED_CODE = 1064;

interface TemporaryErrorState {
  lastAt: number;
  count: number;
  captured: boolean;
}

type InitPhase = 'preload' | 'activate' | 'runtime';

type PreloadRetryPlan = 'scheduled' | 'exhausted';

interface RuntimeErrorContext {
  phase: InitPhase;
  attempt: number;
  onLine: boolean;
  retry: PreloadRetryPlan | null;
}

interface PreloadableInstance {
  preload(): Promise<void>;
}

const logPreloadRetry = (
  outcome: 'scheduled' | 'started' | 'exhausted' | 'recovered',
  fields: { attempt: number; code?: number; delayMs?: number; phase?: InitPhase }
): void => {
  const record = { outcome, ...fields, onLine: navigator.onLine };
  const message = `[VB] preload retry ${outcome}`;
  // Only exhaustion is a failure — a scheduled or recovered retry is the mechanism working.
  const level = outcome === 'exhausted' ? 'warn' : 'info';

  logger.remote(VB_LOG)[level](message, record);
  Sentry.logger[level](message, logsAttributes({ ...record, event: 'vb.preload.retry' }));
};

const INFERENCE_TEST_PASSED_CODE = 1015; // TEST_INFERENCE_PASSED
const GPU_INFERENCE_INCONSISTENCY_CODE = 1011; // the SDK then switches itself to WASM
const CPU_INFERENCE_INCONSISTENCY_CODE = 1012; // no fallback left
const INFERENCE_TEST_CODES = new Set<number>([
  INFERENCE_TEST_PASSED_CODE,
  GPU_INFERENCE_INCONSISTENCY_CODE,
  CPU_INFERENCE_INCONSISTENCY_CODE,
]);

// The SDK's test events carry no data payload, so the fields are read from its message.
const INFERENCE_TEST_SCORE_PATTERN = /score=([\d.]+)/;
// Reasons are a fixed SDK set; the lookahead drops the lengths "length mismatch" carries.
const INFERENCE_TEST_REASON_PATTERN = /reason: ([a-z -]+?)(?=,| \w+=|$)/;
// Fingerprinting protection (Brave) can alter the decoded fixture, making a failure unreliable.
const FIXTURE_MISMATCH_MARKER = 'CRC32 mismatch';

const logInferenceTest = (code: number, sdkMessage: string): void => {
  const passed = code === INFERENCE_TEST_PASSED_CODE;
  const outcome = passed ? 'passed' : 'failed';
  const score = INFERENCE_TEST_SCORE_PATTERN.exec(sdkMessage)?.[1];

  emitTelemetryEvent({
    event: 'vb.inference.test',
    message: `[VB] inference test ${outcome}`,
    // A GPU failure is recovered by the SDK's WASM fallback; only a CPU failure leaves a wrong mask.
    level: code === CPU_INFERENCE_INCONSISTENCY_CODE ? 'warn' : 'info',
    channel: VB_LOG.capture,
    attributes: {
      outcome,
      code,
      score: score === undefined ? undefined : Number(score),
      reason: INFERENCE_TEST_REASON_PATTERN.exec(sdkMessage)?.[1],
      fixtureMismatch: passed ? undefined : sdkMessage.includes(FIXTURE_MISMATCH_MARKER),
      sdkMessage,
    },
  });
};

export class EffectsSDKProvider extends VirtualBackgroundProvider {
  private _instance: any = null;

  private _activated = false;

  private _activeBackground: BackgroundConfig = { type: '', value: '' };

  private _showFps = false;

  private _segmentationPreset = 'balanced';

  private _rejectOnError: ((error: Error) => void) | null = null;

  private _temporaryErrors = new Map<number, TemporaryErrorState>();

  private _capturedUnsupported = false;

  private _capturedSoftwareRendererSkipped = false;

  private _reprobeWindowExhausted = false;

  private _cancelReprobeDelay: (() => void) | null = null;

  private _initAborted = false;

  private _preloadRetryAttempt = 0;

  private _cancelPreloadRetry: (() => void) | null = null;

  get name() {
    return 'effects_sdk';
  }

  get activated() {
    return this._activated;
  }

  get activeBackground() {
    return this._activeBackground;
  }

  get initialized() {
    return !!this._instance;
  }

  private _reprobeDelay(ms: number): Promise<'elapsed' | 'cancelled'> {
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        this._cancelReprobeDelay = null;
        resolve('elapsed');
      }, ms);
      this._cancelReprobeDelay = () => {
        window.clearTimeout(timer);
        this._cancelReprobeDelay = null;
        resolve('cancelled');
      };
    });
  }

  private async _runCapabilityGate(): Promise<GateProbeState> {
    const msSincePageLoad = performance.now();
    const wasm = probeWasm();
    const initialWebGL = probeWebGL();
    const state: GateProbeState = {
      ok: false,
      wasm,
      initialWebGL,
      finalWebGL: initialWebGL,
      webgpu: probeWebGpu(),
      msSincePageLoad,
      reprobeCount: 0,
      recoveredAfterMs: null,
      windowExhaustedEarlier: false,
    };

    let verdict = gateVerdict(wasm, initialWebGL);

    if (verdict === 'no-webgl') {
      if (this._reprobeWindowExhausted) {
        // The window already ran out on this provider, so this initial probe was the single
        // immediate re-check: fail fast rather than re-pay ~30 s per camera toggle.
        state.windowExhaustedEarlier = true;
        return state;
      }

      for (const delayMs of REPROBE_DELAYS_MS) {
        // eslint-disable-next-line no-await-in-loop
        const outcome = await this._reprobeDelay(delayMs);
        // The flag catches an abort that lands between two delays, when the canceller is null.
        if (outcome === 'cancelled' || this._initAborted) {
          throw new VBGateAbortedError();
        }
        state.reprobeCount += 1;
        state.finalWebGL = probeWebGL();
        if (state.finalWebGL.webgl2 || state.finalWebGL.webgl1) {
          state.recoveredAfterMs = Math.round(performance.now() - msSincePageLoad);
          break;
        }
      }

      verdict = gateVerdict(wasm, state.finalWebGL);
      if (verdict === 'no-webgl') {
        this._reprobeWindowExhausted = true;
        return state;
      }
    } else if (this._reprobeWindowExhausted && (initialWebGL.webgl2 || initialWebGL.webgl1)) {
      this._reprobeWindowExhausted = false;
    }

    // 'error' (our probe malfunctioned) proceeds: the gate refuses only on positive evidence.
    state.ok = verdict === 'ok' || verdict === 'error';
    return state;
  }

  private _recordGate(gate: GateProbeState, sdkSupported: boolean | null): GateRecord {
    const record = buildGateRecord(gate, sdkSupported);
    const captureIssue = record.outcome === 'refused' && !this._capturedUnsupported;
    if (captureIssue) {
      this._capturedUnsupported = true;
    }
    emitGateRecord(record, { captureIssue });
    return record;
  }

  // 'runtime' is real: the SDK re-authenticates lazily and re-downloads the model on a GPU→WASM
  // switch while the pipeline is live, so a two-valued phase would misfile those as preload.
  private _currentPhase(): InitPhase {
    if (this._rejectOnError) {
      return 'activate';
    }
    return this._activated ? 'runtime' : 'preload';
  }

  private _runtimeErrorContext(instance: PreloadableInstance, code: unknown): RuntimeErrorContext {
    const phase = this._currentPhase();
    const retryable =
      phase === 'preload' &&
      typeof code === 'number' &&
      PRELOAD_RETRYABLE_CODES.has(code) &&
      this._instance === instance;
    let retry: PreloadRetryPlan | null = null;
    if (retryable) {
      retry =
        this._preloadRetryAttempt < PRELOAD_RETRY_DELAYS_MS.length ? 'scheduled' : 'exhausted';
    }
    return { phase, attempt: this._preloadRetryAttempt, onLine: navigator.onLine, retry };
  }

  // The SDK's only success signal is this INFO line — without it a retry's recovery would be
  // readable only as the absence of a later error, which a session ending mid-retry also produces.
  private _isPreloadRetryRecovery(message: unknown): boolean {
    return (
      this._preloadRetryAttempt > 0 &&
      !this._activated &&
      typeof message === 'string' &&
      // The SDK prefixes every notice it emits with `[ <emitter> ]: `, so this cannot anchor.
      message.includes('Inited ')
    );
  }

  private _schedulePreloadRetry(instance: PreloadableInstance, code: number): void {
    if (this._cancelPreloadRetry) {
      return;
    }
    const delayMs = Math.round(
      PRELOAD_RETRY_DELAYS_MS[this._preloadRetryAttempt] * (0.5 + Math.random())
    );
    logPreloadRetry('scheduled', { code, attempt: this._preloadRetryAttempt + 1, delayMs });

    const timer = window.setTimeout(() => {
      this._cancelPreloadRetry = null;
      if (this._instance !== instance) {
        return;
      }
      this._preloadRetryAttempt += 1;
      logPreloadRetry('started', { code, attempt: this._preloadRetryAttempt });
      // preload() swallows its own failures and resolves at once; the catch is belt and braces.
      instance.preload().catch(noop);
    }, delayMs);
    this._cancelPreloadRetry = () => {
      window.clearTimeout(timer);
      this._cancelPreloadRetry = null;
    };
  }

  async init(): Promise<void> {
    if (this._instance) {
      return;
    }

    this._initAborted = false;

    // The gate runs before the dynamic import: the SDK's memoised capability singleton must not
    // latch a WebGL read taken while the machine is still mid-outage.
    const gate = await this._runCapabilityGate();

    // Refusing before the SDK is constructed is what makes `instance` non-nullable below, so the
    // compiler — not an invariant living in gateTelemetry — guarantees the uses are safe.
    if (!gate.ok) {
      const refusal = this._recordGate(gate, null);
      throw new VBGateRefusedError(refusal.failedCondition ?? 'unknown');
    }

    const { tsvb } = await import('effects-sdk');

    // eslint-disable-next-line new-cap -- SDK exports lowercase constructor
    const instance = new tsvb(process.env.REACT_APP_EFFECTS_SDK_CUSTOMER_ID!);
    // Absolute URLs required — the CPU/WASM backend runs in a Worker that can't resolve relative paths.
    const baseUrl = new URL('/vb', window.location.origin).href;
    instance.config({
      sdk_url: `${baseUrl}/`,
      api_url: licenseUrl(),
      preset: this._segmentationPreset,
      effects: ['virtual_background'],
      // Pinning CPU inference is the only escape: the SDK hands ORT a device it created itself,
      // so ORT's own `adapterInfo` back-compat shim never runs and model init throws as a 1022.
      provider: gate.webgpu === 'adapter-info-missing' ? 'wasm' : 'webgpu',
      // A GPU can return a wrong mask with no error (Adreno 710). The SDK's one-time fixture check
      // at effect start (landscape input only) catches that and switches inference to WASM.
      test_inference: true,
      wasmPaths: {
        wasm: `${baseUrl}/ort-wasm-simd-threaded.asyncify.wasm`,
      },
    });

    // isSupported() is false on a software WebGL renderer (SwiftShader/llvmpipe) — which renders
    // frozen/green frames instead of failing — as well as on missing WebGL1/WASM.
    let sdkSupported: boolean;
    try {
      sdkSupported = instance.isSupported();
    } catch {
      // A throwing vendor read is a refusal like any other — keep the census complete.
      sdkSupported = false;
    }

    const record = this._recordGate(gate, sdkSupported);

    // The rejection flows to the caller's existing raw-camera fallback.
    if (record.outcome === 'refused') {
      throw new VBGateRefusedError(record.failedCondition ?? 'unknown');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    instance.onError((event: any) => {
      const eventDetails = {
        type: event.type,
        code: event.code,
        message: event.message,
        emitter: event.emitter,
        data: event.data,
        cause: event.cause ? serializeError(event.cause) : undefined,
      };

      // The test's own event is the whole record: the generic error path would open a Sentry
      // issue for a fallback the SDK already handles.
      if (INFERENCE_TEST_CODES.has(event.code)) {
        logInferenceTest(event.code, event.message ?? '');
        return;
      }

      if (event.code === SOFTWARE_RENDERER_SKIPPED_CODE) {
        logger
          .remote(VB_LOG)
          .warn('[VB] Software renderer skipped — effects unavailable', eventDetails);
        if (!this._capturedSoftwareRendererSkipped) {
          this._capturedSoftwareRendererSkipped = true;
          Sentry.captureMessage('[VB] Software renderer skipped (1064)', {
            level: 'warning',
            tags: { component: 'VBProvider', operation: 'software-renderer-skipped' },
            extra: { code: event.code, emitter: event.emitter, data: event.data },
          });
        }
        return;
      }

      if (event.type === 'error') {
        const context = this._runtimeErrorContext(instance, event.code);
        logger
          .remote(VB_LOG)
          .error('[VB] VBProvider runtime error', { ...eventDetails, ...context });
        this._captureRuntimeError(event, context);

        const isFatalInit = typeof event.code === 'number' && FATAL_INIT_CODES.has(event.code);
        if (isFatalInit) {
          Sentry.logger.error(
            `[VB] init failed — code ${event.code}`,
            logsAttributes({
              code: event.code,
              emitter: event.emitter,
              ...context,
              event: 'vb.init.error',
            })
          );
        }

        if (context.retry === 'scheduled') {
          this._schedulePreloadRetry(instance, event.code);
        } else if (context.retry === 'exhausted') {
          logPreloadRetry('exhausted', { code: event.code, attempt: context.attempt });
        }

        if (this._rejectOnError && isFatalInit) {
          const error: Error & { code?: number; data?: unknown } = new Error(
            `[VB] SDK initialization failed (${event.code}): ${event.message}`,
            event.cause ? { cause: event.cause } : undefined
          );
          error.code = event.code;
          error.data = event.data;
          this._rejectOnError(error);
          this._rejectOnError = null;
        }
      } else if (event.type === 'warning') {
        logger.remote(VB_LOG).warn('[VB] VBProvider SDK event', eventDetails);
      } else {
        logger.remote(VB_LOG).info('[VB] VBProvider SDK event', eventDetails);
        if (this._isPreloadRetryRecovery(event.message)) {
          // 'activate' means the init landed while a click was in flight — the retry and the
          // activation share one memoised session promise, so the credit is undecidable.
          logPreloadRetry('recovered', {
            attempt: this._preloadRetryAttempt,
            phase: this._currentPhase(),
          });
          // A healthy session ends the episode; the next failure is a new one with a full budget.
          this._preloadRetryAttempt = 0;
        }
      }
    });

    await instance.preload();

    this._instance = instance;
  }

  async activate(options: ActivateBackgroundOptions): Promise<MediaStream | void> {
    if (this._activated) {
      return undefined;
    }

    await this._useStream(options.stream);
    this._instance.run();
    this._syncFpsOverlay();
    this._syncSegmentationPreset();

    const stream = this._instance.getStream();
    if (!stream) {
      return undefined;
    }

    this._applyEffect(options.type, options.value);

    this._activated = true;
    this._activeBackground = { type: options.type, value: options.value };
    return stream;
  }

  async deactivate(): Promise<void> {
    if (this._activated) {
      try {
        this._stopPipeline();
      } catch {
        // deactivation errors are non-fatal
      }
    }

    this._resetState();
  }

  async changeBackground(config: BackgroundConfig): Promise<void> {
    try {
      this._applyEffect(config.type, config.value);
      this._activeBackground = { type: config.type, value: config.value };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'VBProvider', operation: 'changeBackground' },
      });
      logger
        .remote(VB_LOG)
        .error('[VB] VBProvider background change failed', serializeError(error));
      throw error;
    }
  }

  async setInputMedia(options: InputMediaOptions): Promise<MediaStream> {
    this._stopPipeline();

    await this._useStream(options.stream);
    this._instance.run();

    const stream = this._instance.getStream();

    // SDK clears effect configuration on stream switch — re-apply
    this._applyEffect(this._activeBackground.type, this._activeBackground.value);

    return stream;
  }

  reset(): void {
    if (!this._activated) {
      return;
    }

    this._stopPipeline();
    this._resetState();
    this._temporaryErrors.clear();
    this._capturedSoftwareRendererSkipped = false;
  }

  override abortInit(): void {
    this._initAborted = true;
    this._cancelReprobeDelay?.();
    this._cancelPreloadRetry?.();
  }

  async destroy(): Promise<void> {
    this.abortInit();

    if (this._activated) {
      try {
        this._stopPipeline();
      } catch {
        // ensure destroy(true) still runs even if pipeline stop fails
      }
    }

    try {
      if (this._instance) {
        await this._instance.destroy(true);
      }
    } finally {
      this._instance = null;
      this._cancelPreloadRetry?.();
      this._preloadRetryAttempt = 0;
      this._resetState();
      this._temporaryErrors.clear();
      this._capturedUnsupported = false;
      this._capturedSoftwareRendererSkipped = false;
      this._reprobeWindowExhausted = false;
    }
  }

  /**
   * Capture an SDK-runtime error to Sentry. Temporary-Issue codes are throttled by
   * vendor's "persistent if ≥2 within 3 s" rule (captured once per code per session);
   * non-Temporary codes capture immediately.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _captureRuntimeError(event: any, context: RuntimeErrorContext): void {
    const { code } = event as { code?: number };
    const baseMessage = event.message ?? 'VBProvider runtime error';
    const prefix = typeof code === 'number' ? `[${code}] ` : '';

    if (typeof code === 'number' && TEMPORARY_ISSUE_CODES.has(code)) {
      const prior = this._temporaryErrors.get(code);
      if (prior?.captured) {
        // Already captured once this session — vendor's "once per code" rule.
        return;
      }

      const now = Date.now();
      const withinWindow = prior !== undefined && now - prior.lastAt <= PERSISTENCE_WINDOW_MS;
      const count = withinWindow ? prior.count + 1 : 1;

      if (withinWindow) {
        Sentry.captureException(
          new Error(
            `${prefix}${baseMessage} (persistent: ≥2 within ${PERSISTENCE_WINDOW_MS}ms)`,
            event.cause ? { cause: event.cause } : undefined
          ),
          {
            tags: { component: 'VBProvider', operation: 'runtime' },
            extra: {
              code,
              emitter: event.emitter,
              occurrenceCount: count,
              sinceFirstMs: now - prior.lastAt,
              data: event.data,
              cause: event.cause ? serializeError(event.cause) : undefined,
            },
            fingerprint: ['{{ default }}', String(code)],
          }
        );
        this._temporaryErrors.set(code, { lastAt: prior.lastAt, count, captured: true });
        return;
      }

      // First occurrence or late recurrence — anchor a fresh window.
      this._temporaryErrors.set(code, { lastAt: now, count, captured: false });
      return;
    }

    // Intermediate retry failures are Logs-only so a retried sequence adds at most two events
    // to its issue.
    if (context.retry === 'scheduled' && context.attempt > 0) {
      return;
    }

    Sentry.captureException(
      new Error(`${prefix}${baseMessage}`, event.cause ? { cause: event.cause } : undefined),
      {
        tags: { component: 'VBProvider', operation: 'runtime' },
        extra: {
          code,
          emitter: event.emitter,
          data: event.data,
          cause: event.cause ? serializeError(event.cause) : undefined,
          ...context,
        },
        fingerprint: typeof code === 'number' ? ['{{ default }}', String(code)] : undefined,
      }
    );
  }

  /**
   * Feeds a new stream to the SDK and waits for it to be ready.
   * Registers onReady BEFORE calling useStream (mandatory — onReady fires <1ms when preloaded).
   * Rejects on fatal SDK error codes instead of a time-based timeout.
   */
  private _useStream(stream: MediaStream): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // The activation is itself an attempt; keep a single session init in flight.
      this._cancelPreloadRetry?.();
      this._rejectOnError = reject;

      this._instance.onReady = () => {
        this._rejectOnError = null;
        resolve();
      };

      this._instance.useStream(stream);
    });
  }

  private _stopPipeline(): void {
    this._instance.stop();
    this._instance.clear();
  }

  private _resetState(): void {
    this._activated = false;
    this._activeBackground = { type: '', value: '' };
  }

  override setShowFps(show: boolean): void {
    this._showFps = show;
    if (this._activated) {
      this._syncFpsOverlay();
    }
  }

  private _syncFpsOverlay(): void {
    if (this._showFps) {
      this._instance.showFps();
    } else {
      this._instance.hideFps();
    }
  }

  private _syncSegmentationPreset(): void {
    this._instance.setSegmentationPreset(this._segmentationPreset);
  }

  /** Clears the opposite effect type, then applies the new one. */
  private _applyEffect(type: string, value: string): void {
    if (type === 'blur') {
      this._instance.clearBackground();
      this._instance.setBlur(parseFloat(value));
    } else if (type === 'image' || type === 'video') {
      this._instance.clearBlur();
      this._instance.setBackground(value);
    }
  }
}
